
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Search, Calendar, User, ArrowRight } from "lucide-react";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPost, setSelectedPost] = useState<any>(null);

  const categories = ["All", "AI Technology", "Legal Tech", "Case Studies", "Industry News", "Best Practices"];

  const blogPosts = [
    {
      id: 1,
      title: "How AI is Revolutionizing Legal Document Review",
      excerpt: "Explore the latest advances in AI technology and how they're transforming the way legal professionals handle document review.",
      content: "Artificial intelligence is fundamentally changing how legal professionals approach document review. Traditional methods that once required hundreds of hours of manual labor can now be completed in a fraction of the time using advanced AI algorithms. This transformation isn't just about speed – it's about accuracy, cost-effectiveness, and allowing legal professionals to focus on higher-value strategic work. In this comprehensive guide, we'll explore the key technologies driving this change, real-world applications, and what the future holds for AI in legal document review.",
      author: "Dr. Sarah Chen",
      date: "2024-01-15",
      category: "AI Technology",
      image: "/placeholder.svg",
      readTime: "8 min read",
      tags: ["AI", "Document Review", "Legal Tech"]
    },
    {
      id: 2,
      title: "Case Study: 80% Time Reduction in Discovery Process",
      excerpt: "Learn how Martinez & Associates reduced their discovery timeline from weeks to days using ExhibitAI.",
      content: "Martinez & Associates, a mid-size litigation firm, faced a challenging case involving over 50,000 documents across multiple formats. Traditional review methods would have taken their team of 8 attorneys approximately 6 weeks. Using ExhibitAI's AI-powered search capabilities, they completed the same review in just 8 days – an 80% reduction in time. This case study examines their methodology, the specific features that made the difference, and the tangible benefits realized including cost savings of over $200,000 in billable hours.",
      author: "Michael Rodriguez",
      date: "2024-01-10",
      category: "Case Studies",
      image: "/placeholder.svg",
      readTime: "12 min read",
      tags: ["Case Study", "Discovery", "Time Savings"]
    },
    {
      id: 3,
      title: "Best Practices for AI-Powered Legal Research",
      excerpt: "Maximize your efficiency with these proven strategies for AI-assisted legal document search and analysis.",
      content: "Implementing AI in legal research requires more than just deploying new technology – it requires a strategic approach to maximize efficiency and accuracy. This comprehensive guide covers 15 best practices developed from working with over 500 law firms. Topics include optimal query formulation, effective document organization, quality control processes, and integration with existing workflows. We'll also cover common pitfalls to avoid and how to measure the ROI of your AI implementation.",
      author: "Jennifer Adams",
      date: "2024-01-08",
      category: "Best Practices",
      image: "/placeholder.svg",
      readTime: "10 min read",
      tags: ["Best Practices", "Legal Research", "Efficiency"]
    },
    {
      id: 4,
      title: "The Future of Legal Technology: 2024 Predictions",
      excerpt: "Industry experts share their insights on emerging trends in legal technology and what to expect in 2024.",
      content: "As we move through 2024, the legal technology landscape continues to evolve at an unprecedented pace. This year promises significant advances in AI capabilities, increased adoption of cloud-based solutions, and new regulatory frameworks for technology use in legal practice. We've gathered insights from leading industry experts, analyzed market trends, and compiled our top 10 predictions for legal technology in 2024. From generative AI applications to blockchain in legal contracts, discover what innovations will shape the future of legal practice.",
      author: "David Thompson",
      date: "2024-01-05",
      category: "Industry News",
      image: "/placeholder.svg",
      readTime: "15 min read",
      tags: ["Future Tech", "Predictions", "Industry Trends"]
    },
    {
      id: 5,
      title: "Security and Compliance in AI Legal Tools",
      excerpt: "Understanding the critical security considerations when implementing AI tools in legal practice.",
      content: "Security and compliance are paramount when implementing AI tools in legal practice. This detailed analysis covers the essential security frameworks, compliance requirements, and best practices for protecting sensitive legal data. We examine SOC 2 compliance, GDPR considerations, attorney-client privilege in the digital age, and emerging regulatory requirements. Learn how to evaluate AI vendors, implement proper security protocols, and maintain compliance while leveraging the power of artificial intelligence in your legal practice.",
      author: "Lisa Park",
      date: "2024-01-03",
      category: "Legal Tech",
      image: "/placeholder.svg",
      readTime: "11 min read",
      tags: ["Security", "Compliance", "Data Protection"]
    },
    {
      id: 6,
      title: "ROI Calculator: Measuring AI Implementation Success",
      excerpt: "Learn how to calculate and demonstrate the return on investment for AI tools in your legal practice.",
      content: "Demonstrating ROI for AI implementation in legal practice requires careful measurement and analysis of multiple factors. This comprehensive guide provides frameworks for calculating time savings, cost reductions, and quality improvements. We'll walk through real examples from firms of various sizes, provide downloadable ROI calculation templates, and share strategies for presenting results to stakeholders. Understanding these metrics is crucial for justifying continued investment in AI technology and optimizing your implementation strategy.",
      author: "Robert Kim",
      date: "2024-01-01",
      category: "Best Practices",
      image: "/placeholder.svg",
      readTime: "9 min read",
      tags: ["ROI", "Metrics", "Business Case"]
    }
  ];

  const popularPosts = [
    {
      id: 1,
      title: "How AI is Revolutionizing Legal Document Review",
      image: "/placeholder.svg",
      excerpt: "Explore the latest advances in AI technology..."
    },
    {
      id: 2,
      title: "Case Study: 80% Time Reduction in Discovery",
      image: "/placeholder.svg",
      excerpt: "Learn how Martinez & Associates reduced..."
    },
    {
      id: 3,
      title: "Best Practices for AI-Powered Legal Research",
      image: "/placeholder.svg",
      excerpt: "Maximize your efficiency with proven strategies..."
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <span className="text-xl font-bold">ExhibitAI</span>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
          ExhibitAI Blog
        </h1>
        <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
          Stay updated with the latest insights on AI, legal technology, and industry best practices.
        </p>
      </section>

      {/* Search and Filter */}
      <section className="container mx-auto px-6 mb-12">
        <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category.toLowerCase().replace(" ", "-")}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Blog Posts */}
          <div className="lg:col-span-3">
            <div className="grid md:grid-cols-2 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedPost(post)}>
                  <div className="aspect-video bg-muted rounded-t-lg" />
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">{post.category}</Badge>
                      <span className="text-sm text-muted-foreground">{post.readTime}</span>
                    </div>
                    <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                        <Calendar className="w-4 h-4 ml-2" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Popular Posts */}
            <Card>
              <CardHeader>
                <CardTitle>Popular Posts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {popularPosts.map((post) => (
                  <div key={post.id} className="flex gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
                    <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm line-clamp-2 mb-1">{post.title}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">{post.excerpt}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {categories.slice(1).map((category) => (
                    <Badge 
                      key={category} 
                      variant="outline" 
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => setSelectedCategory(category.toLowerCase().replace(" ", "-"))}
                    >
                      {category}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Blog Post Modal */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <div className="aspect-video bg-muted rounded-lg mb-4" />
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary">{selectedPost.category}</Badge>
                  <span className="text-sm text-muted-foreground">{selectedPost.readTime}</span>
                </div>
                <DialogTitle className="text-2xl">{selectedPost.title}</DialogTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>{selectedPost.author}</span>
                  <Calendar className="w-4 h-4 ml-2" />
                  <span>{new Date(selectedPost.date).toLocaleDateString()}</span>
                </div>
              </DialogHeader>
              <div className="prose prose-sm max-w-none">
                <p className="text-lg text-muted-foreground mb-6">{selectedPost.excerpt}</p>
                <div className="whitespace-pre-line">{selectedPost.content}</div>
                <div className="flex flex-wrap gap-2 mt-6">
                  {selectedPost.tags.map((tag: string) => (
                    <Badge key={tag} variant="outline">{tag}</Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Blog;